import React, { memo } from "react";
import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import type { MaterialRequest } from "features/MaterialRequests/types/material-request.types";
import {
  materialRequestEligibleForApproveReject,
  materialRequestEligibleForCancel,
  materialRequestEligibleForDelete,
  materialRequestEligibleForEdit,
} from "features/MaterialRequests/utils/material-request.utils";

const DEFAULT_DETAIL_PATH = (id: number) =>
  `/inventory-management/material-requests/${id}`;

const APPROVE_REJECT_DISABLED_HINT =
  "Only requests awaiting approval can be approved or rejected.";

export type MaterialRequestActionsDropdownProps = {
  record: MaterialRequest;
  canRead: boolean;
  canApprove: boolean;
  canReject: boolean;
  canUpdate: boolean;
  canDelete?: boolean;
  onApprove: (record: MaterialRequest) => void;
  onReject: (record: MaterialRequest) => void;
  onCancel: (record: MaterialRequest) => void;
  onDelete?: (record: MaterialRequest) => void;
  /** Override link target for “View” (defaults to inventory MR detail). */
  detailPath?: (id: number) => string;
};

function MaterialRequestActionsDropdownInner({
  record,
  canRead,
  canApprove,
  canReject,
  canUpdate,
  canDelete = false,
  onApprove,
  onReject,
  onCancel,
  onDelete,
  detailPath = DEFAULT_DETAIL_PATH,
}: MaterialRequestActionsDropdownProps) {
  const canApproveRejectAct = materialRequestEligibleForApproveReject(record);
  const canCancelRow = canUpdate && materialRequestEligibleForCancel(record);
  const canEditRow = canUpdate && materialRequestEligibleForEdit(record);
  const canDeleteRow = canDelete && materialRequestEligibleForDelete(record);

  const hasActions = canRead || canApprove || canReject || canCancelRow || canEditRow || canDeleteRow;

  const showPrimaryBlock = canRead || canEditRow || canApprove || canReject;
  const showDividerBeforeCancel = showPrimaryBlock && canCancelRow;
  const showDividerBeforeDelete = (showPrimaryBlock || canCancelRow) && canDeleteRow;

  if (!hasActions) return null;

  return (
    <UncontrolledDropdown>
      <DropdownToggle
        type="button"
        className="btn btn-soft-secondary btn-sm"
        tag="button"
        aria-label="Material request actions"
      >
        <i className="ri-more-2-fill align-middle" />
      </DropdownToggle>
      <DropdownMenu container="body" end>
        {canRead ? (
          <DropdownItem tag={Link} to={detailPath(record.id)}>
            <i className="ri-eye-line align-bottom me-2 text-muted" />
            View
          </DropdownItem>
        ) : null}
        {canEditRow ? (
          <DropdownItem
            tag={Link}
            to={`/inventory-management/material-requests/${record.id}/edit`}
          >
            <i className="ri-pencil-line align-bottom me-2 text-muted" />
            Edit
          </DropdownItem>
        ) : null}
        {canApprove ? (
          <DropdownItem
            onClick={() => onApprove(record)}
            disabled={!canApproveRejectAct}
            title={
              !canApproveRejectAct ? APPROVE_REJECT_DISABLED_HINT : undefined
            }
          >
            <i className="ri-check-double-line align-bottom me-2 text-muted" />
            Approve
          </DropdownItem>
        ) : null}
        {canReject ? (
          <DropdownItem
            onClick={() => onReject(record)}
            disabled={!canApproveRejectAct}
            title={
              !canApproveRejectAct ? APPROVE_REJECT_DISABLED_HINT : undefined
            }
          >
            <i className="ri-close-circle-line align-bottom me-2 text-muted" />
            Reject
          </DropdownItem>
        ) : null}
        {canCancelRow ? (
          <>
            {showDividerBeforeCancel ? <DropdownItem divider /> : null}
            <DropdownItem
              onClick={() => onCancel(record)}
              className="text-danger"
            >
              <i className="ri-forbid-line align-bottom me-2 text-muted" />
              Cancel request
            </DropdownItem>
          </>
        ) : null}
        {canDeleteRow ? (
          <>
            {showDividerBeforeDelete ? <DropdownItem divider /> : null}
            <DropdownItem
              onClick={() => onDelete?.(record)}
              className="text-danger"
            >
              <i className="ri-delete-bin-line align-bottom me-2" />
              Delete
            </DropdownItem>
          </>
        ) : null}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
}

export default memo(MaterialRequestActionsDropdownInner);
